import { doc, getDoc } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { eventSettingsSchema, type EventSettings } from "./schemas";

export type EventSettingsState =
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "error"; error: string }
  | { status: "success"; data: EventSettings };

// A3: events/{eventId} by doc id.
export function useEventSettings(eventId: string): EventSettingsState & {
  reload: () => void;
} {
  const [state, setState] = useState<EventSettingsState>({
    status: "loading",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    getDoc(doc(db, "events", eventId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setState({ status: "not-found" });
          return;
        }
        const result = eventSettingsSchema.safeParse(snap.data());
        setState(
          result.success
            ? { status: "success", data: result.data }
            : {
                // Not "not found": the event exists and the admin may read it.
                // Reporting a shape mismatch as a missing document hid two
                // real bugs elsewhere in this app already.
                status: "error",
                error: "This event's settings could not be read.",
              },
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: err instanceof Error ? err.message : "Failed to load event",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, reload };
}
